'use client';

import React, { useCallback, useEffect, useState } from 'react';
import usePropertyListing from '@src/v3/hooks/usePropertyListing';
import {
  getAllRenterSavesProperty,
  getProperties,
} from '@src/v3/services/properties';
import { transformAllPublicPropertiesData } from '@src/v3/utils/helper';
import { useRouter } from 'next/navigation';
import {
  getSavedPropertyIds,
  getUserType,
  isLoggedIn,
} from '@src/v3/utils/localStorage';
import PropertyCard from '@src/v3/components/propertyCard';
import PropertyListShimmer from '@src/v3/components/propertyListShimmer';
import Icon from '@src/v3/components/coreUI/icon';
import {
  Property,
  AllGetSavedProperty,
} from '@src/v3/interfaces/app/propertyLists';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import Button from '@src/v3/components/coreUI/button';
import { APP_ROUTE } from '@src/v3/constants/routes';
import Image from 'next/image';
import {
  deleteRenterunSaveSync,
  postRenterSaveSync,
} from '@src/v3/services/properties';
import {
  DeleteUnsaveRenterSyncPayload,
  PostSaveRenterSyncPayload,
} from '@src/v3/interfaces/app/propertyLists';
import {
  getUserId,
  setSavedPropertyId,
  removeSavedPropertyId,
} from '@src/v3/utils/localStorage';

const SavedListingContent = () => {
  // Hooks
  const { propertyList = [], setPropertyList } = usePropertyListing();
  const router = useRouter();

  // States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRole, setIsRole] = useState<string>('');
  const [totalProperties, setTotalProperties] = useState<number>(0);

  const userType = getUserType();

  /**
   * This method is used for save renter property
   */
  const renterSaveProperty = async (propertyId: string, userId: string) => {
    try {
      const payload: PostSaveRenterSyncPayload = {
        renter_id: userId,
        property_ids: [propertyId],
      };
      await postRenterSaveSync(payload);

      // Always update the property list
      const updatedPropertyList: Property[] = propertyList.map(
        (property: Property) =>
          property.id === propertyId
            ? { ...property, isSave: true }
            : { ...property }
      );
      setPropertyList(updatedPropertyList);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * This method is used for unsave renter property
   */
  const renterUnsaveProperty = async (propertyId: string) => {
    try {
      const payload: DeleteUnsaveRenterSyncPayload = {
        property_id: propertyId,
      };
      await deleteRenterunSaveSync(payload);

      // Always update the property list, even if saves array is empty
      const updatedPropertyList: Property[] = propertyList.map(
        (property: Property) =>
          property.id === propertyId
            ? { ...property, isSave: false }
            : { ...property }
      );
      setPropertyList(updatedPropertyList);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * This method is used for save/unsave button event
   */
  const handleSaveUnsaveProperty = (propertyId: string, isSave: boolean) => {
    if (!isLoggedIn()) {
      const updatedPropertyList: Property[] = propertyList.map(
        (property: Property) =>
          property.id === propertyId
            ? { ...property, isSave: !property.isSave }
            : { ...property }
      );
      if (isSave) {
        removeSavedPropertyId(propertyId);
      } else {
        setSavedPropertyId(propertyId);
      }
      setPropertyList(updatedPropertyList);
      return;
    }

    const userId = getUserId() ?? '';
    if (userType === ZEME_USERS_ROLE.RENTER) {
      if (!isSave) {
        renterSaveProperty(propertyId, userId);
      } else {
        renterUnsaveProperty(propertyId);
      }
    }
  };

  /**
   * Fetch all saved listings for renter
   */
  const fetchAllSavedListingRenter = useCallback(async () => {
    try {
      setIsLoading(true);
      const getSavedListIds = getSavedPropertyIds();

      if (userType === ZEME_USERS_ROLE.RENTER) {
        const { saves = [] }: { saves: AllGetSavedProperty[] } =
          await getAllRenterSavesProperty();

        if (saves.length) {
          const propertyIds: string[] = saves.map(
            ({ property_id }) => property_id
          );
          const getFetchPropertyURL = `?properties_by_list=${[...propertyIds]}`;
          const { results = [] } = await getProperties(getFetchPropertyURL);
          const properties: Property[] =
            transformAllPublicPropertiesData(results);
          setPropertyList(
            properties.map((property) => ({ ...property, isSave: true }))
          );
          setTotalProperties(properties.length);
        } else {
          setPropertyList([]);
          setTotalProperties(0);
        }
      } else if (!isLoggedIn() && getSavedListIds.length) {
        const getFetchPropertyURL = `?properties_by_list=${[
          ...getSavedListIds,
        ]}`;
        const { results = [] } = await getProperties(getFetchPropertyURL);
        const properties: Property[] =
          transformAllPublicPropertiesData(results);
        setPropertyList(
          properties.map((property) => ({ ...property, isSave: true }))
        );
        setTotalProperties(properties.length);
      } else {
        setPropertyList([]);
        setTotalProperties(0);
      }
    } catch (error) {
      console.error(error);
      setPropertyList([]);
      setTotalProperties(0);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  useEffect(() => {
    fetchAllSavedListingRenter();
  }, [fetchAllSavedListingRenter]);

  // Monitor property list changes to detect when properties are unsaved
  useEffect(() => {
    // Filter out properties that are no longer saved
    const savedProperties = propertyList.filter((property) => property.isSave);

    // Update total properties count based on saved properties
    if (savedProperties.length !== totalProperties) {
      setTotalProperties(savedProperties.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyList]);

  // lifecycle hook for check role with each render
  useEffect(() => {
    const role = getUserType();
    const isLoggedin = isLoggedIn();
    if (!isLoggedin) {
      return setIsRole('renter');
    }
    return setIsRole(role);
  }, []);

  // Filter to only show saved properties
  const savedPropertiesList = propertyList.filter(
    (property) => property.isSave
  );

  // Show empty state when no saved properties and not loading
  if (
    !isLoading &&
    (savedPropertiesList.length === 0 || totalProperties === 0)
  ) {
    return (
      <div className="w-full px-5 sm:px-10 pb-20 sm:pb-10">
        <div className="pt-8">
          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-medium text-black-dark">Saved</h1>
          </div>

          {/* Empty State */}
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-full text-center flex flex-col items-center">
              <p className="text-balck-dark text-base">
                Seems like there&apos;s no listings
              </p>
              <Image
                src="/agent-empty-listing.png"
                alt="No saved properties"
                width={450}
                height={230}
              />
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={() => router.push(APP_ROUTE.EXPLORE)}
              className="rounded-full max-w-[158px]"
            >
              Start Exploring
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-5 sm:px-10 pb-20 sm:pb-10">
      <div className="pt-8">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-medium text-black-dark">Saved</h1>
        </div>

        {/* Listing Property */}
        <div className="">
          {/* Skelton loader */}
          {isLoading && (
            <div className="grid grid-cols-290 gap-x-5 gap-y-5 mx-auto">
              <PropertyListShimmer />
            </div>
          )}

          {/* Listings */}
          <div className="grid grid-cols-290 gap-x-6 gap-y-8 sm:gap-y-12 mx-auto">
            {!isLoading &&
              savedPropertiesList &&
              savedPropertiesList?.map((property: Property) => {
                const image =
                  property.imageInformation.images.length > 0
                    ? (property.imageInformation.images[0].thumbnail as string)
                    : '';

                return (
                  <PropertyCard
                    key={property.id}
                    id={property.id}
                    role={isRole}
                    isMyListing={
                      (isRole === ZEME_USERS_ROLE.AGENT ||
                        isRole === ZEME_USERS_ROLE.LANDLORD) &&
                      property?.generalInformation?.agent?.id === getUserId()
                    }
                    image={image}
                    name={property.generalInformation?.propertyName}
                    price={property.economicInformation?.cost}
                    updatedPrice={property.economicInformation?.updatedCost}
                    address={property.generalInformation?.address}
                    bath={property.generalInformation?.bathrooms}
                    bedroom={property.generalInformation?.bedrooms}
                    squareFootage={property.generalInformation?.squareFootage}
                    unitCode={property.generalInformation?.address?.unitCode}
                    isEasyApply={
                      property.generalInformation?.isEasyApply || false
                    }
                    handleSaveUnsaveProperty={(id: string, isSave: boolean) =>
                      handleSaveUnsaveProperty(id, isSave)
                    }
                    isSaved={Boolean(property?.isSave)}
                    isExclusive={property.generalInformation?.isExclusive}
                    netEffectiveRent={
                      property?.economicInformation?.netEffectiveRent
                    }
                    leaseConcession={
                      property?.economicInformation?.leaseConcession
                    }
                    leaseMonthsFree={String(
                      property?.economicInformation?.leaseMonthsFree
                    )}
                    leaseTerm={property?.economicInformation?.leaseTerm}
                    brokerFee={property?.chargeInformation?.brokerFee}
                  />
                );
              })}
          </div>

          {/**No result found */}
          {!isLoading && savedPropertiesList?.length == 0 && (
            <div className="flex items-center justify-center text-center mt-8">
              <div className="bg-primary-dark/10 flex items-center justify-center h-10 w-10 rounded-lg text-primary-dark mr-3">
                <Icon
                  iconName="noresultfound"
                  iconProps={{ className: 'h-6 w-6' }}
                />
              </div>
              <span className="font-medium">No results found..</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedListingContent;
